import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  QrCode, 
  Camera, 
  User, 
  Star, 
  Gift, 
  X, 
  Check,
  Loader2,
  Coffee,
  Cake,
  Percent,
  RotateCcw,
  ArrowRightLeft,
  Ticket,
  Crown,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface MemberData {
  id: string;
  first_name: string;
  last_name: string;
  member_number: string | null;
  points_balance: number;
  current_level: number;
  status: string;
}

interface Reward {
  id: string;
  name: string;
  description: string | null;
  points_required: number;
  category: string;
}

interface PrizeData {
  prize_id: string;
  user_id: string;
  status: string;
  level_earned: number;
  reward: {
    id: string;
    name: string;
    description: string | null;
    rarity: string;
  };
  user_name: string;
}

interface LoyaltyReaderProps {
  staffPinId: string;
  locationId: string;
}

type ScanMode = 'idle' | 'member' | 'prize';

const LoyaltyReader: React.FC<LoyaltyReaderProps> = ({ staffPinId, locationId }) => {
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Member mode state
  const [member, setMember] = useState<MemberData | null>(null);
  const [availableRewards, setAvailableRewards] = useState<Reward[]>([]);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [addingPoints, setAddingPoints] = useState(false);
  const [redeemingReward, setRedeemingReward] = useState<Reward | null>(null);
  const [processingRedeem, setProcessingRedeem] = useState(false);
  
  // Prize mode state
  const [scanMode, setScanMode] = useState<ScanMode>('idle');
  const [prizeData, setPrizeData] = useState<PrizeData | null>(null);
  const [confirmingPrize, setConfirmingPrize] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Initialize video playback when stream is assigned
  useEffect(() => {
    if (scanning && videoRef.current && streamRef.current) {
      const video = videoRef.current;
      video.srcObject = streamRef.current;
      video.play().catch((err) => {
        console.error('Error playing video:', err);
        setCameraError('No se pudo reproducir el video de la cámara');
      });
    }
  }, [scanning]);

  const startCamera = async () => {
    setCameraError(null);
    
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Tu navegador no soporta acceso a la cámara');
      toast.error('Navegador no compatible con cámara');
      return;
    }

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      setScanning(true);
      
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      
      let errorMessage = 'No se pudo acceder a la cámara';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Permiso de cámara denegado. Activa el permiso en la configuración del navegador.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No se encontró ninguna cámara en el dispositivo';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'La cámara está siendo usada por otra aplicación';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'No se encontró una cámara compatible';
      }
      
      setCameraError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScanning(false);
    setCameraError(null);
  };

  // Smart QR parser - detects type and routes accordingly
  const parseAndProcessQR = async (qrData: string) => {
    setLoading(true);
    
    try {
      // Try to parse as JSON first
      let parsed: any = null;
      try {
        parsed = JSON.parse(qrData);
      } catch {
        // Not JSON, treat as member ID
      }

      if (parsed && parsed.type === 'prize' && parsed.token) {
        // Prize QR detected
        setScanMode('prize');
        await lookupPrize(parsed.token);
      } else if (parsed && parsed.type === 'ticket' && parsed.token) {
        // Ticket QR - inform staff this is for customer scanning
        toast.info('QR de Ticket detectado', {
          description: 'Este QR es para que el cliente escanee en la app y gane puntos.',
        });
        setLoading(false);
      } else {
        // Member QR or manual ID
        setScanMode('member');
        let memberId = qrData.trim();
        if (memberId.startsWith('REYES-MEMBER:')) {
          memberId = memberId.replace('REYES-MEMBER:', '');
        }
        await lookupMember(memberId);
      }
    } catch (err: any) {
      toast.error('Error al procesar QR', { description: err.message });
      setLoading(false);
    }
  };

  const lookupMember = async (memberId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('get-loyalty-member', {
        body: { 
          member_user_id: memberId,
          staff_token: staffPinId,
        },
      });

      if (error) throw error;

      if (data.success) {
        setMember(data.profile);
        setAvailableRewards(data.available_rewards || []);
        stopCamera();
      } else {
        throw new Error(data.error || 'Socio no encontrado');
      }
    } catch (err: any) {
      toast.error('Socio no encontrado', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const lookupPrize = async (prizeToken: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-prize-qr', {
        body: { 
          prize_qr_token: prizeToken,
          staff_token: staffPinId,
          action: 'validate',
        },
      });

      if (error) throw error;

      if (data.success) {
        setPrizeData({
          prize_id: data.prize_id,
          user_id: data.user_id,
          status: data.status,
          level_earned: data.level_earned,
          reward: data.reward,
          user_name: data.user_name,
        });
        stopCamera();
      } else {
        throw new Error(data.error || 'Premio no válido');
      }
    } catch (err: any) {
      toast.error('Premio no válido', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = () => {
    if (!manualInput.trim()) return;
    parseAndProcessQR(manualInput.trim());
  };

  const handleAddPoints = async () => {
    if (!member || !purchaseAmount) return;

    const amount = parseFloat(purchaseAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Introduce un importe válido');
      return;
    }

    setAddingPoints(true);
    try {
      const { data, error } = await supabase.functions.invoke('add-loyalty-points', {
        body: {
          member_user_id: member.id,
          purchase_amount: amount,
          staff_pin_id: staffPinId,
          location_id: locationId,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`¡+${data.points_added} puntos añadidos!`, {
          description: `Nuevo saldo: ${data.new_balance} puntos`,
        });
        
        setMember(prev => prev ? {
          ...prev,
          points_balance: data.new_balance,
        } : null);
        
        setPurchaseAmount('');
      } else {
        throw new Error(data.error);
      }
    } catch (err: any) {
      toast.error('Error al añadir puntos', { description: err.message });
    } finally {
      setAddingPoints(false);
    }
  };

  const handleRedeemReward = async () => {
    if (!member || !redeemingReward) return;

    setProcessingRedeem(true);
    try {
      const { data, error } = await supabase.functions.invoke('redeem-reward', {
        body: {
          member_user_id: member.id,
          reward_id: redeemingReward.id,
          staff_pin_id: staffPinId,
          location_id: locationId,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`🎁 ¡Premio canjeado!`, {
          description: `${data.reward_name} (-${data.points_spent} pts). Nuevo saldo: ${data.new_balance} pts`,
        });

        setMember(prev => prev ? {
          ...prev,
          points_balance: data.new_balance,
        } : null);

        setAvailableRewards(prev => 
          prev.filter(r => r.points_required <= data.new_balance)
        );
        
        setRedeemingReward(null);
      } else {
        throw new Error(data.error);
      }
    } catch (err: any) {
      toast.error('Error al canjear premio', { description: err.message });
    } finally {
      setProcessingRedeem(false);
    }
  };

  const handleConfirmPrize = async () => {
    if (!prizeData) return;

    setConfirmingPrize(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-prize-qr', {
        body: { 
          prize_qr_token: prizeData.prize_id,
          staff_token: staffPinId,
          location_id: locationId,
          action: 'redeem',
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success('🎉 ¡Premio entregado!', {
          description: `${prizeData.reward.name} marcado como canjeado`,
        });
        resetSearch();
      } else {
        throw new Error(data.error);
      }
    } catch (err: any) {
      toast.error('Error al confirmar premio', { description: err.message });
    } finally {
      setConfirmingPrize(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bebida':
        return <Coffee className="h-4 w-4" />;
      case 'dulce':
        return <Cake className="h-4 w-4" />;
      case 'descuento':
        return <Percent className="h-4 w-4" />;
      default:
        return <Gift className="h-4 w-4" />;
    }
  };

  const getRarityStyles = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white border-yellow-600';
      case 'epic':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-600';
      case 'rare':
        return 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white border-blue-500';
      default:
        return 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-800 border-gray-400';
    }
  };

  const resetSearch = () => {
    setMember(null);
    setPrizeData(null);
    setAvailableRewards([]);
    setPurchaseAmount('');
    setManualInput('');
    setRedeemingReward(null);
    setScanMode('idle');
  };

  const renderIdleMode = () => (
    <>
      {/* Camera Section */}
      <div 
        id="loyalty-camera-reader" 
        className="relative bg-muted rounded-lg overflow-hidden"
        style={{ minHeight: '300px' }}
      >
        {scanning ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ minHeight: '300px' }}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-secondary rounded-lg" />
            </div>
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2 z-10"
              onClick={stopCamera}
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 py-8" style={{ minHeight: '300px' }}>
            {cameraError ? (
              <>
                <div className="text-destructive text-center px-4">
                  <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">{cameraError}</p>
                </div>
                <Button onClick={startCamera} variant="outline">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reintentar
                </Button>
              </>
            ) : (
              <>
                <Camera className="h-12 w-12 text-muted-foreground" />
                <Button onClick={startCamera}>
                  <Camera className="h-4 w-4 mr-2" />
                  Activar Cámara
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* QR Type Info */}
      <div className="grid grid-cols-2 gap-2 text-center text-xs">
        <div className="p-2 bg-secondary/10 rounded-lg border border-secondary/30">
          <User className="h-4 w-4 mx-auto mb-1 text-secondary" />
          <span className="font-medium">QR Socio</span>
          <p className="text-muted-foreground">Ver perfil y canjear puntos</p>
        </div>
        <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <Crown className="h-4 w-4 mx-auto mb-1 text-amber-500" />
          <span className="font-medium">QR Premio</span>
          <p className="text-muted-foreground">Entregar premio de nivel</p>
        </div>
      </div>

      {/* Manual Input */}
      <div className="space-y-2">
        <Label htmlFor="manual-id">Búsqueda manual (QR o ID)</Label>
        <div className="flex gap-2">
          <Input
            id="manual-id"
            placeholder="Pega aquí el contenido del QR..."
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
          />
          <Button onClick={handleManualSearch} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Buscar'
            )}
          </Button>
        </div>
      </div>
    </>
  );

  const renderMemberMode = () => (
    <>
      {/* Member Info */}
      <Card className="border-secondary/50 bg-secondary/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-secondary/20 rounded-full">
              <User className="h-8 w-8 text-secondary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">
                {member?.first_name} {member?.last_name}
              </h3>
              <p className="text-sm text-muted-foreground">
                Socio #{member?.member_number || 'Pendiente'} • Nivel {member?.current_level || 1}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Star className="h-4 w-4 text-secondary" />
                <span className="font-bold text-secondary text-lg">
                  {member?.points_balance} puntos
                </span>
              </div>
            </div>
            <Badge variant={member?.status === 'activo' ? 'default' : 'secondary'}>
              {member?.status}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Add Points */}
      <div className="space-y-2">
        <Label htmlFor="purchase-amount">Importe de la compra (€)</Label>
        <div className="flex gap-2">
          <Input
            id="purchase-amount"
            type="number"
            step="0.01"
            min="0"
            placeholder="15.50"
            value={purchaseAmount}
            onChange={(e) => setPurchaseAmount(e.target.value)}
          />
          <Button 
            onClick={handleAddPoints} 
            disabled={addingPoints || !purchaseAmount}
            className="min-w-[140px]"
          >
            {addingPoints ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                +{Math.floor(parseFloat(purchaseAmount) || 0)} puntos
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">1€ = 1 punto</p>
      </div>

      {/* Available Rewards */}
      {availableRewards.length > 0 && (
        <div>
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Gift className="h-4 w-4 text-secondary" />
            Premios canjeables ({availableRewards.length})
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableRewards.map((reward) => (
              <div 
                key={reward.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border hover:bg-muted/80 transition-colors"
              >
                <div className="flex items-center gap-2 flex-1">
                  <div className="p-1.5 bg-secondary/20 rounded text-secondary">
                    {getCategoryIcon(reward.category)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{reward.name}</span>
                    {reward.description && (
                      <p className="text-xs text-muted-foreground truncate">{reward.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <Badge variant="secondary" className="text-xs whitespace-nowrap">
                    {reward.points_required} pts
                  </Badge>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => setRedeemingReward(reward)}
                    className="h-8"
                  >
                    <ArrowRightLeft className="h-3 w-3 mr-1" />
                    Canjear
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reset */}
      <Button variant="outline" onClick={resetSearch} className="w-full">
        <RotateCcw className="h-4 w-4 mr-2" />
        Buscar otro socio
      </Button>
    </>
  );

  const renderPrizeMode = () => (
    <>
      {/* Prize Verification Card */}
      <Card className={`border-2 ${getRarityStyles(prizeData?.reward.rarity || 'common')}`}>
        <CardContent className="p-6 text-center">
          <div className="mb-4">
            <Sparkles className="h-12 w-12 mx-auto mb-2" />
            <Badge className="mb-2 uppercase tracking-wide">
              {prizeData?.reward.rarity || 'common'}
            </Badge>
          </div>
          
          <h3 className="text-xl font-bold mb-2">
            {prizeData?.reward.name}
          </h3>
          
          {prizeData?.reward.description && (
            <p className="text-sm opacity-90 mb-4">
              {prizeData.reward.description}
            </p>
          )}

          <div className="border-t border-current/20 pt-4 mt-4 text-sm">
            <p className="font-medium">Cliente: {prizeData?.user_name}</p>
            <p className="opacity-75">Premio de Nivel {prizeData?.level_earned}</p>
          </div>
        </CardContent>
      </Card>

      {prizeData?.status === 'active' ? (
        <div className="space-y-3">
          <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-300 dark:border-green-700 text-center">
            <Check className="h-6 w-6 mx-auto mb-1 text-green-600" />
            <p className="font-medium text-green-800 dark:text-green-200">PREMIO VÁLIDO</p>
            <p className="text-sm text-green-600 dark:text-green-400">Listo para entregar</p>
          </div>

          <Button 
            onClick={handleConfirmPrize} 
            disabled={confirmingPrize}
            className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
          >
            {confirmingPrize ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Gift className="h-5 w-5 mr-2" />
            )}
            Entregar y Marcar como Usado
          </Button>
        </div>
      ) : (
        <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700 text-center">
          <X className="h-6 w-6 mx-auto mb-1 text-red-600" />
          <p className="font-medium text-red-800 dark:text-red-200">PREMIO YA CANJEADO</p>
          <p className="text-sm text-red-600 dark:text-red-400">Este QR ya no es válido</p>
        </div>
      )}

      <Button variant="outline" onClick={resetSearch} className="w-full">
        <RotateCcw className="h-4 w-4 mr-2" />
        Escanear otro QR
      </Button>
    </>
  );

  return (
    <Card className="max-w-lg mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5 text-secondary" />
          Lector de Socio
          {scanMode === 'prize' && (
            <Badge variant="outline" className="ml-auto bg-amber-100 text-amber-800 border-amber-300">
              <Crown className="h-3 w-3 mr-1" />
              Modo Premio
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {scanMode === 'idle' && 'Escanea el QR del cliente (socio o premio)'}
          {scanMode === 'member' && 'Gestiona puntos y canjea recompensas'}
          {scanMode === 'prize' && 'Verifica y entrega el premio de nivel'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {scanMode === 'idle' && !member && !prizeData && renderIdleMode()}
        {scanMode === 'member' && member && renderMemberMode()}
        {scanMode === 'prize' && prizeData && renderPrizeMode()}
      </CardContent>

      {/* Confirm Redeem Dialog (for point-based rewards) */}
      <AlertDialog open={!!redeemingReward} onOpenChange={(open) => !open && setRedeemingReward(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-secondary" />
              Confirmar canje de premio
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {redeemingReward && member && (
                <>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="font-medium text-foreground">{redeemingReward.name}</p>
                    {redeemingReward.description && (
                      <p className="text-sm text-muted-foreground mt-1">{redeemingReward.description}</p>
                    )}
                    <p className="text-lg font-bold text-secondary mt-2">
                      -{redeemingReward.points_required} puntos
                    </p>
                  </div>
                  <p className="text-sm">
                    <strong>{member.first_name}</strong> tiene actualmente <strong>{member.points_balance} puntos</strong>.
                    Tras el canje tendrá <strong>{member.points_balance - redeemingReward.points_required} puntos</strong>.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Esta acción no se puede deshacer.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingRedeem}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRedeemReward}
              disabled={processingRedeem}
              className="bg-secondary hover:bg-secondary/90"
            >
              {processingRedeem ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Confirmar canje
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default LoyaltyReader;
